/**
 * MapSidebar Component
 * Right sidebar showing stats for selected/hovered area
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import WcIcon from '@mui/icons-material/Wc';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { formatNumber, GeoFeatureData } from './mapUtils';

interface MapSidebarProps {
  open: boolean;
  areaName: string;
  areaLevel: string;
  data: GeoFeatureData | null;
  onClose: () => void;
  childAreas?: { name: string; value: number }[];
  onDrillDown?: () => void;
  canDrillDown?: boolean;
}

const MapSidebar: React.FC<MapSidebarProps> = ({
  open,
  areaName,
  areaLevel,
  data,
  onClose,
  childAreas,
  onDrillDown,
  canDrillDown,
}) => {
  if (!open || !data) return null;

  const totalStudents = data.total_students || 0;
  const maleStudents = data.male_students || 0;
  const femaleStudents = data.female_students || 0;
  const totalSchools = data.total_schools || 0;
  const genderParity = data.gender_parity || 0;
  const femalePct = totalStudents > 0 ? Math.round((femaleStudents / totalStudents) * 100) : 0;

  return (
    <Paper
      elevation={4}
      sx={{
        width: 280,
        height: '100%',
        overflow: 'auto',
        borderLeft: '3px solid #1976D2',
        backgroundColor: '#FAFAFA',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #1565C0, #1976D2)',
          color: 'white',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {areaName}
          </Typography>
          <Chip
            label={areaLevel}
            size="small"
            sx={{
              mt: 0.5,
              height: 20,
              fontSize: '0.65rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
            }}
          />
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white', mt: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ p: 1.5 }}>
        {/* Enrollment */}
        <Box
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: 2,
            backgroundColor: 'white',
            border: '1px solid #E3F2FD',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <PeopleIcon sx={{ fontSize: 18, color: '#1976D2', mr: 0.5 }} />
            <Typography variant="caption" color="textSecondary">
              Total Enrollment
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565C0' }}>
            {formatNumber(totalStudents)}
          </Typography>
        </Box>

        {/* Gender Breakdown */}
        <Box
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: 2,
            backgroundColor: 'white',
            border: '1px solid #E8F5E9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <WcIcon sx={{ fontSize: 18, color: '#388E3C', mr: 0.5 }} />
            <Typography variant="caption" color="textSecondary">
              Gender Distribution
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#1565C0', fontWeight: 600 }}>
                Male
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatNumber(maleStudents)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#C2185B', fontWeight: 600 }}>
                Female
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatNumber(femaleStudents)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Female: {femalePct}%
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                GPI: {genderParity.toFixed(2)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={femalePct}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#BBDEFB',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: femalePct >= 45 && femalePct <= 55 ? '#4CAF50' : '#FF9800',
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </Box>

        {/* Schools */}
        <Box
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: 2,
            backgroundColor: 'white',
            border: '1px solid #FFF3E0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <SchoolIcon sx={{ fontSize: 18, color: '#E65100', mr: 0.5 }} />
            <Typography variant="caption" color="textSecondary">
              Schools
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#E65100' }}>
            {formatNumber(totalSchools)}
          </Typography>
          {totalSchools > 0 && totalStudents > 0 && (
            <Typography variant="caption" color="textSecondary">
              Avg {formatNumber(Math.round(totalStudents / totalSchools))} students/school
            </Typography>
          )}
        </Box>

        {/* Drill down button */}
        {canDrillDown && onDrillDown && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ZoomInMapIcon />}
            onClick={onDrillDown}
            fullWidth
            sx={{
              mt: 0.5,
              mb: 1,
              textTransform: 'none',
              fontSize: '0.75rem',
              borderColor: '#1976D2',
              color: '#1976D2',
              '&:hover': {
                backgroundColor: '#E3F2FD',
                borderColor: '#1565C0',
              },
            }}
          >
            View Wards in {areaName}
          </Button>
        )}
      </Box>

      {/* Child Areas List (e.g., districts within region) */}
      {childAreas && childAreas.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: '#666', mr: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                Sub-areas ({childAreas.length})
              </Typography>
            </Box>
            <List dense disablePadding>
              {childAreas
                .sort((a, b) => b.value - a.value)
                .map((area, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 0.3,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { backgroundColor: '#F5F5F5' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {area.name}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565C0' }}>
                            {formatNumber(area.value)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
            </List>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default MapSidebar;
